import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { listTasks } from './api';
const PAGE_LIMIT = 20;
const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Pending', value: 'pending' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Blocked', value: 'blocked' },
    { label: 'Complete', value: 'complete' },
];
function statusLabel(status) {
    switch (status) {
        case 'pending':
        case 'unassigned':
            return 'Pending';
        case 'in_progress': return 'In Progress';
        case 'blocked': return 'Blocked';
        case 'complete': return 'Complete';
        default: return 'Pending';
    }
}
function statusBadgeStyle(status) {
    switch (status) {
        case 'complete':
            return {
                display: 'inline-block',
                padding: '3px 10px',
                backgroundColor: 'rgba(52,199,89,0.12)',
                color: '#248a3d',
                borderRadius: '980px',
                fontSize: '12px',
                fontWeight: '600',
            };
        case 'in_progress':
            return {
                display: 'inline-block',
                padding: '3px 10px',
                backgroundColor: 'rgba(0,122,255,0.12)',
                color: '#0071e3',
                borderRadius: '980px',
                fontSize: '12px',
                fontWeight: '600',
            };
        case 'blocked':
            return {
                display: 'inline-block',
                padding: '3px 10px',
                backgroundColor: 'rgba(255,59,48,0.12)',
                color: '#c0392b',
                borderRadius: '980px',
                fontSize: '12px',
                fontWeight: '600',
            };
        default:
            return {
                display: 'inline-block',
                padding: '3px 10px',
                backgroundColor: 'rgba(255,149,0,0.12)',
                color: '#b45309',
                borderRadius: '980px',
                fontSize: '12px',
                fontWeight: '600',
            };
    }
}
export const TaskList = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [tokenStack, setTokenStack] = useState([undefined]);
    const [pageIndex, setPageIndex] = useState(0);
    const currentToken = tokenStack[pageIndex];
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await listTasks({
                status: statusFilter || undefined,
                limit: PAGE_LIMIT,
                nextToken: currentToken,
            });
            setResult(data);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load tasks');
        }
        finally {
            setLoading(false);
        }
    }, [statusFilter, currentToken]);
    useEffect(() => {
        load();
    }, [load]);
    const handleTabChange = (value) => {
        setStatusFilter(value);
        setTokenStack([undefined]);
        setPageIndex(0);
    };
    const handleNext = () => {
        if (!result?.nextToken)
            return;
        const next = pageIndex + 1;
        const newStack = tokenStack.slice(0, next);
        newStack.push(result.nextToken);
        setTokenStack(newStack);
        setPageIndex(next);
    };
    const handlePrev = () => {
        if (pageIndex === 0)
            return;
        setPageIndex(pageIndex - 1);
    };
    const formatDate = (d) => {
        if (!d)
            return '—';
        const [year, month, day] = d.split('-');
        return `${month}/${day}/${year}`;
    };
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsxs("div", { style: styles.pageHeader, children: [_jsxs("div", { children: [_jsx("h2", { style: styles.heading, children: "Internal Tasks" }), _jsx("p", { style: styles.subheading, children: "Manage and track internal work items" })] }), _jsxs("div", { style: styles.headerActions, children: [_jsx("button", { style: styles.secondaryButton, onClick: () => navigate('/tasks/templates'), children: "Manage Templates" }), _jsx("button", { style: styles.primaryButton, onClick: () => navigate('/tasks/new'), children: "+ New Task" })] })] }), _jsx("div", { style: styles.tabsContainer, children: _jsx("div", { style: { ...styles.tabsRow, overflowX: isMobile ? 'auto' : 'visible' }, children: STATUS_TABS.map((tab) => (_jsx("button", { style: {
                                ...styles.tab,
                                ...(statusFilter === tab.value ? styles.tabActive : {}),
                            }, onClick: () => handleTabChange(tab.value), children: tab.label }, tab.value))) }) }), error && _jsx("div", { style: styles.errorBanner, children: error }), loading && !result && (_jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) })), result && result.items.length === 0 && !loading && (_jsx("p", { style: styles.emptyText, children: "No tasks found." })), result && result.items.length > 0 && (_jsx("div", { style: { opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s ease' }, children: isMobile ? (_jsx("div", { children: result.items.map((task) => (_jsx(TaskMobileCard, { task: task, onClick: () => navigate(`/tasks/${task.id}`), formatDate: formatDate, statusLabel: statusLabel, statusBadgeStyle: statusBadgeStyle }, task.id))) })) : (_jsxs("div", { style: styles.listCard, children: [_jsxs("div", { style: styles.tableHeader, children: [_jsx("span", { style: { ...styles.col, flex: 3 }, children: "Task" }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: "Status" }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: "Due Date" }), _jsx("span", { style: { ...styles.col, flex: 2 }, children: "Assigned To" }), _jsx("span", { style: { ...styles.col, flex: 1 }, children: "Notes" })] }), result.items.map((task, idx) => (_jsx(TaskRow, { task: task, isLast: idx === result.items.length - 1, onClick: () => navigate(`/tasks/${task.id}`), formatDate: formatDate, statusLabel: statusLabel, statusBadgeStyle: statusBadgeStyle }, task.id)))] })) })), result && (result.items.length > 0 || pageIndex > 0) && (_jsxs("div", { style: styles.pagination, children: [_jsx("button", { style: styles.pageButton, onClick: handlePrev, disabled: pageIndex === 0, children: "\u2190 Previous" }), _jsxs("span", { style: styles.pageInfo, children: ["Page ", pageIndex + 1] }), _jsx("button", { style: styles.pageButton, onClick: handleNext, disabled: !result?.nextToken, children: "Next \u2192" })] }))] }) }));
};
const TaskMobileCard = ({ task, onClick, formatDate, statusLabel, statusBadgeStyle, }) => {
    const [hovered, setHovered] = useState(false);
    return (_jsxs("div", { style: {
            backgroundColor: hovered ? 'rgba(0,122,255,0.04)' : '#fff',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            minHeight: '44px',
        }, onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                }, children: [_jsx("span", { style: { fontSize: '16px', fontWeight: '700', color: '#1d1d1f', flex: 1, marginRight: '8px' }, children: task.name }), _jsx("span", { style: statusBadgeStyle(task.status), children: statusLabel(task.status) })] }), _jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', marginTop: '4px' }, children: [_jsx("span", { style: { fontSize: '13px', color: '#86868b' }, children: task.assignedInstallerName ?? 'Unassigned' }), _jsx("span", { style: { fontSize: '13px', color: '#86868b' }, children: task.dueDate ? `Due ${formatDate(task.dueDate)}` : 'No due date' })] }), task.notes.length > 0 && (_jsx("div", { style: { marginTop: '4px' }, children: _jsxs("span", { style: { fontSize: '12px', color: '#86868b' }, children: [task.notes.length, " note", task.notes.length !== 1 ? 's' : ''] }) }))] }));
};
const TaskRow = ({ task, isLast, onClick, formatDate, statusLabel, statusBadgeStyle, }) => {
    const [hovered, setHovered] = useState(false);
    return (_jsxs("div", { style: {
            display: 'flex',
            padding: '16px 24px',
            alignItems: 'center',
            transition: 'background-color 0.15s ease',
            borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.06)',
            backgroundColor: hovered ? 'rgba(0,122,255,0.04)' : 'transparent',
            cursor: 'pointer',
        }, onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx("span", { style: {
                    flex: 3,
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1d1d1f',
                    paddingRight: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }, children: task.name }), _jsx("span", { style: { flex: 1.5, paddingRight: '12px' }, children: _jsx("span", { style: statusBadgeStyle(task.status), children: statusLabel(task.status) }) }), _jsx("span", { style: { flex: 1.5, fontSize: '13px', color: '#86868b', paddingRight: '12px' }, children: task.dueDate ? formatDate(task.dueDate) : '—' }), _jsx("span", { style: { flex: 2, fontSize: '13px', color: '#3c3c43', paddingRight: '12px' }, children: task.assignedInstallerName ?? 'Unassigned' }), _jsx("span", { style: { flex: 1, fontSize: '13px', color: '#86868b' }, children: task.notes.length })] }));
};
const styles = {
    pageHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '12px',
    },
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '17px',
        fontWeight: '400',
        margin: 0,
        color: '#86868b',
    },
    headerActions: {
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    primaryButton: {
        padding: '12px 24px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        letterSpacing: '-0.2px',
        whiteSpace: 'nowrap',
        minHeight: '44px',
    },
    secondaryButton: {
        padding: '11px 20px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        color: '#0071e3',
        border: '1px solid rgba(0,113,227,0.3)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        minHeight: '44px',
    },
    tabsContainer: {
        marginBottom: '16px',
    },
    tabsRow: {
        display: 'flex',
        gap: '8px',
        padding: '4px 0',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
    },
    tab: {
        padding: '8px 18px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#3c3c43',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        minHeight: '36px',
        transition: 'all 0.15s ease',
    },
    tabActive: {
        backgroundColor: '#0071e3',
        color: '#fff',
        border: '1px solid #0071e3',
        fontWeight: '600',
    },
    errorBanner: {
        padding: '14px 20px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    listCard: {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        overflow: 'hidden',
    },
    tableHeader: {
        display: 'flex',
        padding: '12px 24px',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        backgroundColor: 'rgba(0,0,0,0.02)',
    },
    col: {
        fontSize: '13px',
        color: '#86868b',
        fontWeight: '500',
        letterSpacing: '-0.1px',
        paddingRight: '12px',
    },
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '60px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    emptyText: {
        textAlign: 'center',
        padding: '60px 24px',
        color: '#86868b',
        fontSize: '16px',
        margin: 0,
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '24px',
    },
    pageButton: {
        padding: '10px 20px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#1d1d1f',
        cursor: 'pointer',
        minHeight: '44px',
    },
    pageInfo: {
        fontSize: '14px',
        color: '#86868b',
        fontWeight: '500',
    },
};
//# sourceMappingURL=TaskList.js.map